import { ChevronLeft } from 'lucide-react';
import { type ReactElement } from 'react';

const GENRE_KEYS = ['g1', 'g2', 'g3'] as const;
const STAT_KEYS = ['s1', 's2', 's3'] as const;
const TAB_KEYS = ['t1', 't2', 't3'] as const;
const SYNOPSIS_KEYS = ['sy1', 'sy2', 'sy3', 'sy4', 'sy5'] as const;
const SIDEBAR_KEYS = ['mb1', 'mb2', 'mb3', 'mb4', 'mb5', 'mb6'] as const;
const RELATED_KEYS = ['r1', 'r2', 'r3', 'r4'] as const;

function BackHeaderSkeleton(): ReactElement {
  return (
    <div
      className="absolute left-0 right-0 top-0 z-40"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 lg:px-8">
        <div className="py-4 sm:py-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/40 bg-background/70 px-4 py-1.5 shadow-sm backdrop-blur-md">
            <ChevronLeft className="h-4 w-4 text-muted-foreground/60" />
            <span className="text-sm text-muted-foreground/60 sm:text-base">
              Back to Home
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroBannerSkeleton(): ReactElement {
  return (
    <div className="skeleton-shimmer relative h-[300px] w-full md:h-[400px]">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background/80" />
    </div>
  );
}

function PosterSkeleton(): ReactElement {
  return (
    <div className="relative mx-auto w-fit flex-shrink-0 lg:mx-0">
      <div className="skeleton-shimmer w-[160px] overflow-hidden rounded-lg shadow-2xl ring-2 ring-border sm:w-[180px] md:w-[230px]">
        <div className="aspect-[2/3] w-full" />
      </div>
      <div className="absolute -right-3 top-2 h-7 w-14 rounded-l-full bg-background/90 shadow-lg sm:-right-4 sm:top-4 sm:h-9 sm:w-16" />
    </div>
  );
}

function TitleBlockSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="skeleton-shimmer h-6 w-12 rounded-full" />
        {GENRE_KEYS.map((key) => (
          <div key={key} className="skeleton-shimmer h-6 w-20 rounded-full" />
        ))}
      </div>
      <div className="skeleton-shimmer h-8 w-3/4 rounded sm:h-10 md:h-12" />
      <div className="skeleton-shimmer h-4 w-1/2 rounded" />
    </div>
  );
}

function QuickStatsSkeleton(): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
      {STAT_KEYS.map((key) => (
        <div
          key={key}
          className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 sm:gap-3 sm:rounded-xl sm:px-4 sm:py-3"
        >
          <div className="skeleton-shimmer h-4 w-4 rounded sm:h-5 sm:w-5" />
          <div className="flex-1 space-y-1.5">
            <div className="skeleton-shimmer h-3 w-12 rounded" />
            <div className="skeleton-shimmer h-3 w-20 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function HeroCardSkeleton(): ReactElement {
  return (
    <div className="relative z-10">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
          <PosterSkeleton />
          <div className="mt-8 flex-1 rounded-xl bg-background/80 p-4 shadow-2xl backdrop-blur-md sm:mt-0 sm:rounded-2xl sm:p-6 md:p-8">
            <div className="space-y-4 sm:space-y-5">
              <TitleBlockSkeleton />
              <div className="flex flex-wrap gap-2">
                <div className="skeleton-shimmer h-10 w-32 rounded-full" />
                <div className="skeleton-shimmer h-10 w-10 rounded-full" />
              </div>
              <QuickStatsSkeleton />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabsBarSkeleton(): ReactElement {
  return (
    <div className="flex flex-wrap gap-2 border-b border-border/40 pb-2">
      {TAB_KEYS.map((key, index) => (
        <div
          key={key}
          className={`skeleton-shimmer h-9 rounded-md ${
            index === 0 ? 'w-24' : 'w-20'
          }`}
        />
      ))}
    </div>
  );
}

function SynopsisSkeleton(): ReactElement {
  return (
    <section className="space-y-3 rounded-xl border border-border/60 bg-card/60 p-4 sm:p-6">
      <div className="skeleton-shimmer h-5 w-32 rounded" />
      <div className="space-y-2">
        {SYNOPSIS_KEYS.map((key) => (
          <div key={key} className="skeleton-shimmer h-3 w-full rounded" />
        ))}
        <div className="skeleton-shimmer h-3 w-3/4 rounded" />
      </div>
    </section>
  );
}

function MetaSidebarSkeleton(): ReactElement {
  return (
    <aside className="space-y-3 rounded-xl border border-border/60 bg-card/60 p-4 sm:p-6">
      <div className="skeleton-shimmer h-5 w-24 rounded" />
      <div className="space-y-3">
        {SIDEBAR_KEYS.map((key) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="skeleton-shimmer h-3 w-20 rounded" />
            <div className="skeleton-shimmer h-3 w-24 rounded" />
          </div>
        ))}
      </div>
    </aside>
  );
}

function RelatedSkeleton(): ReactElement {
  return (
    <section className="space-y-3">
      <div className="skeleton-shimmer h-5 w-40 rounded" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {RELATED_KEYS.map((key) => (
          <div key={key} className="space-y-2">
            <div className="skeleton-shimmer aspect-[2/3] w-full rounded-lg border border-border/40" />
            <div className="skeleton-shimmer h-3 w-4/5 rounded" />
            <div className="skeleton-shimmer h-3 w-1/2 rounded" />
          </div>
        ))}
      </div>
    </section>
  );
}

function TabsContentSkeleton(): ReactElement {
  return (
    <div className="relative">
      <div className="mx-auto mt-[280px] max-w-[1400px] px-3 pb-16 sm:mt-[200px] sm:px-6 md:mt-[240px] lg:px-8">
        <div className="flex flex-col space-y-6">
          <TabsBarSkeleton />
          <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-[minmax(0,1fr),350px] lg:gap-12">
            <div className="space-y-6">
              <SynopsisSkeleton />
              <RelatedSkeleton />
            </div>
            <MetaSidebarSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
}

export function MangaDetailSkeleton(): ReactElement {
  // RouteSkeleton owns the delay; paint instantly so subsequent
  // internal re-mounts don't introduce a reset gap.
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-background">
      <BackHeaderSkeleton />
      <HeroBannerSkeleton />
      <HeroCardSkeleton />
      <TabsContentSkeleton />
    </div>
  );
}
